import {
  TEntityResponse,
  TEntityListResponse,
  TCreateFlowTriggerReqBody,
  TFlowTriggerResBody,
  TFlowTriggerSummaryResBody,
  TUpdateFlowTriggerReqBody,
} from '@poseidon-city/shared';

import { TAPIClient } from '../client';

type TFlowTriggersClient = {
  create(
    body: TCreateFlowTriggerReqBody,
    dry: boolean
  ): Promise<TEntityResponse<'flow-trigger', TFlowTriggerResBody> | undefined>;
  update(
    id: string,
    body: TUpdateFlowTriggerReqBody,
    dry: boolean
  ): Promise<TEntityResponse<'flow-trigger', TFlowTriggerResBody> | undefined>;
  delete(id: string): Promise<void>;
  get(id: string): Promise<TEntityResponse<'flow-trigger', TFlowTriggerResBody> | undefined>;
  list(
    limit: number,
    page: number
  ): Promise<TEntityListResponse<'flow-trigger', TFlowTriggerSummaryResBody> | undefined>;
};

function FlowTriggersClient(apiClient: TAPIClient): TFlowTriggersClient {
  return {
    async create(
      body: TCreateFlowTriggerReqBody,
      _dry?: boolean
    ): Promise<TEntityResponse<'flow-trigger', TFlowTriggerResBody> | undefined> {
      const data = await apiClient.post<
        TCreateFlowTriggerReqBody,
        TEntityResponse<'flow-trigger', TFlowTriggerResBody>
      >('/flow-triggers', body);

      if (data && data.type !== 'flow-trigger') {
        throw new Error('Invalid entity type received, expected: flow-trigger, received: ' + data.type);
      }

      return data;
    },

    async update(
      id: string,
      body: TUpdateFlowTriggerReqBody,
      _dry?: boolean
    ): Promise<TEntityResponse<'flow-trigger', TFlowTriggerResBody> | undefined> {
      const data = await apiClient.put<TUpdateFlowTriggerReqBody, TEntityResponse<'flow-trigger', TFlowTriggerResBody>>(
        `/flow-triggers/${id}`,
        body
      );

      if (data && data.type !== 'flow-trigger') {
        throw new Error('Invalid entity type received, expected: flow-trigger, received: ' + data.type);
      }

      return data;
    },

    async delete(id: string): Promise<void> {
      return await apiClient.delete(`/flow-triggers/${id}`);
    },

    async get(id: string): Promise<TEntityResponse<'flow-trigger', TFlowTriggerResBody> | undefined> {
      const data = await apiClient.get<TEntityResponse<'flow-trigger', TFlowTriggerResBody>>(`/flow-triggers/${id}`);

      if (data && data.type !== 'flow-trigger') {
        throw new Error('Invalid entity type received, expected: flow-trigger, received: ' + data.type);
      }

      return data;
    },

    async list(
      limit: number,
      page: number
    ): Promise<TEntityListResponse<'flow-trigger', TFlowTriggerSummaryResBody> | undefined> {
      const data = await apiClient.get<TEntityListResponse<'flow-trigger', TFlowTriggerSummaryResBody>>(
        `/flow-triggers?limit=${limit}&page=${page}`
      );

      if (data && data.type !== 'flow-trigger') {
        throw new Error('Invalid entity type received, expected: flow-trigger, received: ' + data.type);
      }

      return data;
    },
  };
}

export { TFlowTriggersClient, FlowTriggersClient };
